import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCoachSession } from "@/hooks/useCoachSession";
import { useInvite } from "@/hooks/useInvite";
import { useWakeLock } from "@/hooks/useWakeLock";
import { applyBranding } from "@/lib/branding";
import { BrandLockup } from "@/shell/CandidateFlow";
import { Waveform } from "@/components/Waveform";
import type { CoachState } from "@/lib/audio/AudioEngine";

// Live interview session (Jinja: session.html — the dark voice UI). The audio
// engine and WS protocol are the Phase-0 spike (AudioEngine + useCoachSession);
// this screen is the UI shell around it: mic-gate, waveform, timer, transcript,
// controls. On end/complete it routes to the scorecard, which owns generation.
//
// Deferred vs Jinja (noted in the migration handoff): the "speak to test your
// mic" calibration loop is replaced by a single permission gate (engine creates
// the mic context inside the Start gesture, the iOS-critical part); cancel routes
// home without an explicit server cancel call.

const STATE_LABEL: Record<CoachState, string> = {
  idle: "READY",
  connecting: "CONNECTING",
  listening: "LISTENING",
  speaking: "SPEAKING",
  thinking: "THINKING…",
  paused: "PAUSED",
  complete: "COMPLETE",
  error: "ERROR",
};

const PAUSE_LABEL: Record<CoachState, string> = {
  idle: "Tap to begin",
  connecting: "Connecting…",
  listening: "Tap to pause",
  speaking: "Coach speaking…",
  thinking: "Thinking…",
  paused: "Tap to resume",
  complete: "Session complete",
  error: "Error",
};

function fmt(secs: number) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function LiveSession() {
  const { token, sessionId } = useParams<{ token: string; sessionId: string }>();
  const navigate = useNavigate();
  const { state, error, start, pause, resume, nextQuestion, end } =
    useCoachSession(sessionId ?? "");
  const { data: invite } = useInvite(token);

  const [gateOpen, setGateOpen] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (invite?.branding) applyBranding(invite.branding);
  }, [invite]);

  // Timer: runs once the session is live, stops on terminal states.
  useEffect(() => {
    if (state === "idle" || state === "connecting" || state === "complete" || state === "error") return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  // On natural completion (coach ended or time cap), go to the scorecard.
  useEffect(() => {
    if (state === "complete") {
      const t = setTimeout(() => navigate(`/invite/${token}/scorecard/${sessionId}`), 2500);
      return () => clearTimeout(t);
    }
  }, [state, navigate, token, sessionId]);

  async function confirmMic() {
    if (started.current) return;
    started.current = true;
    setGateOpen(false);
    try {
      await start();
    } catch {
      /* engine surfaces the error via state/error */
    }
  }

  function endAndScore() {
    end();
    setTimeout(() => navigate(`/invite/${token}/scorecard/${sessionId}`), 600);
  }

  function togglePause() {
    if (state === "paused") resume();
    else if (state === "listening" || state === "speaking" || state === "thinking") pause();
  }

  function cancel() {
    if (!window.confirm("Cancel interview? No scorecard will be generated.")) return;
    navigate(`/invite/${token}`);
  }

  const waveActive = state === "listening" || state === "speaking";
  const live = state !== "idle" && state !== "connecting" && state !== "complete" && state !== "error";
  const showNext = state === "listening" || state === "speaking" || state === "thinking";

  useWakeLock(live);

  return (
    <div className="relative flex min-h-screen flex-col bg-paper text-ink">
      {/* Brand lockup */}
      <div className="flex justify-center px-4 pt-4 pb-1">
        <BrandLockup
          agencyName={invite?.agencyName}
          logoUrl={
            invite?.branding?.logoStyle === "variant"
              ? (invite?.branding?.logoUrlDark || invite?.branding?.logoUrl || undefined)
              : (invite?.branding?.logoUrl || undefined)
          }
          logoFilter={
            invite?.branding?.logoStyle === "auto_invert" && invite?.branding?.logoUrl
              ? "brightness(0) invert(1)"
              : undefined
          }
        />
      </div>

      {/* Topbar — 3-equal-column grid keeps timer truly centred */}
      <header className="grid grid-cols-3 items-center gap-2 px-4 py-3 sm:px-6">
        <div>
          <button onClick={cancel} className="text-sm text-slate-500 hover:text-ink">← Exit</button>
        </div>
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2">
            {live && <span className="h-2 w-2 animate-pulse rounded-full bg-crit" />}
            <span className="font-mono text-[13px] tabular-nums text-slate-500">{fmt(elapsed)}</span>
          </div>
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--accent)" }}>
            {STATE_LABEL[state]}
          </span>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={endAndScore}
            disabled={state === "idle" || state === "connecting"}
            className="rounded-pill bg-accent px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
          >
            End &amp; Score
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="flex flex-1 flex-col items-center justify-center gap-7 px-6 text-center">

        <Waveform active={waveActive} />

        {state === "thinking" && (
          <span className="inline-flex items-center gap-2 rounded-pill bg-warn-50 px-3 py-1.5 text-xs font-semibold text-warn">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warn" /> Thinking…
          </span>
        )}

        {showNext && (
          <button onClick={nextQuestion} className="rounded-pill border border-hair px-4 py-2 text-xs text-slate-500 hover:bg-paper-2">
            Next question →
          </button>
        )}

        <p className="text-xs text-slate-500">Take your time. Pause to gather a thought.</p>

        {state === "error" ? (
          <div className="flex max-w-sm flex-col items-center gap-3">
            <p className="text-sm text-crit">
              We couldn&apos;t {elapsed > 0 ? "continue" : "start"} your interview session.
            </p>
            {error && <p className="text-xs text-slate-500">{error}</p>}
            <button
              onClick={() => window.location.reload()}
              className="rounded-pill bg-accent px-5 py-2.5 text-sm font-semibold text-white"
            >
              Try again
            </button>
            <button
              onClick={() => navigate(`/invite/${token}/home`)}
              className="text-xs text-slate-400 hover:text-slate-200"
            >
              Back to home
            </button>
          </div>
        ) : (
          error && <p className="max-w-sm text-sm text-crit">{error}</p>
        )}
        {state === "complete" && (
          <p className="text-sm text-slate-500">Interview complete. Generating your scorecard…</p>
        )}
      </main>

      {/* Bottom: pause control */}
      <footer className="flex flex-col items-center gap-4 px-6 pb-8 pt-2">
        <button
          onClick={togglePause}
          disabled={!live}
          aria-label={PAUSE_LABEL[state]}
          className={`flex h-[76px] w-[76px] items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
            state === "paused" ? "bg-accent text-white" : "bg-paper-2 text-ink-3 border border-hair"
          }`}
        >
          {state === "paused" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z" /></svg>
          )}
        </button>
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-400">{PAUSE_LABEL[state]}</span>
      </footer>

      {/* Mic gate modal */}
      {gateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/20 px-6 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl bg-cream border border-hair p-6 text-center shadow-lg">
            <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent-50 text-accent">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
                <path d="M5 11a7 7 0 0014 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <h2 className="text-lg font-semibold text-ink">We need your microphone</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              This is a voice interview. When your browser asks for microphone access, click{" "}
              <strong>Allow</strong>.
            </p>
            <button
              onClick={confirmMic}
              className="mt-5 w-full rounded-pill bg-accent px-5 py-3 text-sm font-semibold text-white"
            >
              My microphone is working. Start interview
            </button>
            <button onClick={cancel} className="mt-3 text-xs text-slate-400 hover:text-slate-600">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
